import { useState, useEffect } from 'react';
import { Link as LinkIcon, Download, FileUp, RefreshCw, Database, Settings, History, Loader, Upload, FileJson, FileText, CheckCircle, AlertCircle } from 'lucide-react';
import importExportService from '../services/importExport';

export default function Chaine() {
  const [activeTab, setActiveTab] = useState('import-api');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [importHistory, setImportHistory] = useState([]);
  const [file, setFile] = useState(null);

  const tabs = [
    { id: 'import-api', label: 'Import API RNCP', icon: Download },
    { id: 'import-manuel', label: 'Import Manuel', icon: FileUp },
    { id: 'historique', label: 'Historique', icon: History },
    { id: 'configuration', label: 'Configuration', icon: Settings }
  ];

  useEffect(() => {
    if (activeTab === 'historique') {
      loadImportHistory();
    }
  }, [activeTab]);

  const loadImportHistory = async () => {
    setLoading(true);
    try {
      const response = await importExportService.getImportHistory();
      setImportHistory(response.data || []);
    } catch (err) {
      console.error('Erreur chargement historique:', err);
      // Mock data si erreur
      setImportHistory([
        {
          id: 1,
          date: '2025-01-15',
          type: 'API RNCP',
          status: 'success',
          recordsImported: 9374,
          duration: '2m 34s'
        },
        {
          id: 2,
          date: '2025-01-10',
          type: 'CSV Manuel',
          status: 'success',
          recordsImported: 156,
          duration: '12s'
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadRncp = async (autoImport = false) => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const response = await importExportService.downloadRncpData(autoImport);
      setSuccess(`Téléchargement réussi ! ${response.filesFound || 0} fichiers trouvés.`);
      if (autoImport) {
        setSuccess('Import automatique lancé avec succès !');
      }
    } catch (err) {
      setError('Erreur lors du téléchargement des données RNCP');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    e.preventDefault();
    if (!file) {
      setError('Veuillez sélectionner un fichier');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await importExportService.importRncpData(formData);
      setSuccess(`Fichier importé avec succès ! ${response.recordsImported || 0} enregistrements.`);
      setFile(null);
      // Reset file input
      e.target.reset();
    } catch (err) {
      setError('Erreur lors de l\'import du fichier');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-12">
          <Loader className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      );
    }

    switch (activeTab) {
      case 'import-api':
        return (
          <div className="space-y-6">
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Download className="w-5 h-5 text-blue-600" />
                Téléchargement automatique depuis France Compétences
              </h3>
              <p className="text-sm text-gray-600 mb-6">
                Télécharge les données RNCP officielles depuis l'API data.gouv.fr (dernière version disponible)
              </p>

              {error && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
                  <AlertCircle className="w-5 h-5" />
                  {error}
                </div>
              )}

              {success && (
                <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2 text-green-700">
                  <CheckCircle className="w-5 h-5" />
                  {success}
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <button
                  onClick={() => handleDownloadRncp(false)}
                  disabled={loading}
                  className="flex items-center justify-center gap-2 px-6 py-3 bg-blue-500 text-white rounded-lg font-medium hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Download className="w-5 h-5" />
                  Télécharger uniquement
                </button>

                <button
                  onClick={() => handleDownloadRncp(true)}
                  disabled={loading}
                  className="flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-lg font-medium hover:from-blue-600 hover:to-indigo-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <RefreshCw className="w-5 h-5" />
                  Télécharger et importer
                </button>
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 border border-gray-200">
              <h4 className="font-semibold text-gray-900 mb-3">Informations</h4>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <span>Connexion directe à l'API officielle data.gouv.fr</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <span>Données au format CSV (dernière mise à jour)</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <span>Parsing automatique et nettoyage des doublons</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <span>Archivage des versions précédentes</span>
                </li>
              </ul>
            </div>
          </div>
        );

      case 'import-manuel':
        return (
          <div className="space-y-6">
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Upload className="w-5 h-5 text-green-600" />
                Import manuel de fichiers
              </h3>

              {error && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
                  <AlertCircle className="w-5 h-5" />
                  {error}
                </div>
              )}

              {success && (
                <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2 text-green-700">
                  <CheckCircle className="w-5 h-5" />
                  {success}
                </div>
              )}

              <form onSubmit={handleFileUpload} className="space-y-4">
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-green-500 transition-colors">
                  <input
                    type="file"
                    accept=".csv,.json,.xml,.xlsx"
                    onChange={(e) => setFile(e.target.files[0])}
                    className="hidden"
                    id="file-upload"
                  />
                  <label
                    htmlFor="file-upload"
                    className="cursor-pointer flex flex-col items-center gap-3"
                  >
                    {file ? (
                      <>
                        <FileText className="w-12 h-12 text-green-600" />
                        <div>
                          <p className="font-medium text-gray-900">{file.name}</p>
                          <p className="text-sm text-gray-500">{(file.size / 1024).toFixed(2)} KB</p>
                        </div>
                      </>
                    ) : (
                      <>
                        <FileUp className="w-12 h-12 text-gray-400" />
                        <div>
                          <p className="font-medium text-gray-900">Cliquez pour sélectionner un fichier</p>
                          <p className="text-sm text-gray-500">CSV, JSON, XML, XLSX (max 50 MB)</p>
                        </div>
                      </>
                    )}
                  </label>
                </div>

                <button
                  type="submit"
                  disabled={!file || loading}
                  className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg font-medium hover:from-green-600 hover:to-emerald-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Upload className="w-5 h-5" />
                  Importer le fichier
                </button>
              </form>
            </div>

            <div className="bg-white rounded-xl p-6 border border-gray-200">
              <h4 className="font-semibold text-gray-900 mb-3">Formats supportés</h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                  <FileJson className="w-5 h-5 text-blue-600" />
                  <span className="text-sm font-medium">JSON</span>
                </div>
                <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                  <FileText className="w-5 h-5 text-green-600" />
                  <span className="text-sm font-medium">CSV</span>
                </div>
                <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                  <FileText className="w-5 h-5 text-orange-600" />
                  <span className="text-sm font-medium">XML</span>
                </div>
                <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                  <FileText className="w-5 h-5 text-purple-600" />
                  <span className="text-sm font-medium">XLSX</span>
                </div>
              </div>
            </div>
          </div>
        );

      case 'historique':
        return (
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <History className="w-5 h-5 text-gray-600" />
              Historique des imports
            </h3>

            {importHistory.length > 0 ? (
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Enregistrements</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Durée</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {importHistory.map((item) => (
                      <tr key={item.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {new Date(item.date).toLocaleDateString('fr-FR')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{item.type}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                            item.status === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                          }`}>
                            {item.status === 'success' ? 'Réussi' : 'Échec'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {item.recordsImported.toLocaleString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{item.duration}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
                <History className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-600">Aucun historique d'import disponible</p>
              </div>
            )}
          </div>
        );

      case 'configuration':
        return (
          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-gray-200">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Settings className="w-5 h-5 text-gray-600" />
                Paramètres d'import
              </h3>

              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-900">Nettoyage automatique des doublons</p>
                    <p className="text-sm text-gray-600">Supprimer les entrées en double lors de l'import</p>
                  </div>
                  <input type="checkbox" defaultChecked className="w-5 h-5 text-blue-600" />
                </div>

                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-900">Validation des données</p>
                    <p className="text-sm text-gray-600">Vérifier la conformité des données avant import</p>
                  </div>
                  <input type="checkbox" defaultChecked className="w-5 h-5 text-blue-600" />
                </div>

                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-900">Archivage des versions</p>
                    <p className="text-sm text-gray-600">Conserver les anciennes versions des référentiels</p>
                  </div>
                  <input type="checkbox" defaultChecked className="w-5 h-5 text-blue-600" />
                </div>

                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-900">Notifications par email</p>
                    <p className="text-sm text-gray-600">Recevoir un email après chaque import</p>
                  </div>
                  <input type="checkbox" className="w-5 h-5 text-blue-600" />
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white shadow-lg">
              <LinkIcon className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-3xl font-heading font-bold text-gray-900">Chaîne</h1>
              <p className="text-gray-600">Import référentiels - Connexion aux bases officielles</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-2 mb-6">
          <div className="flex flex-wrap gap-2">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-all ${
                    activeTab === tab.id
                      ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-md'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="hidden sm:inline">{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          {renderContent()}
        </div>
      </div>
    </div>
  );
}
